import {encodeGlobalId} from '../../forecast-app/shared/util/GlobalIdUtil';
import Util from '../../forecast-app/shared/util/util';
import {
	BaselineItem,
	BaselineTotals,
	Deliverable,
	ExpenseRow,
	Phase,
	Project,
	ProjectPhaseDeliverable,
	RoleRow,
} from './BaselineTypes';

export const PHASE_COLUMNS = {
	PHASE_NAME: 'phase_name',
	ROLE: 'role',
	EXPENSE_NAME: 'expense_name',
	EXPENSE_CATEGORY: 'expense_category',
	DELIVERABLE_NAME: 'deliverable_name',
	HOURS: 'hours',
	VALUE_OF_SERVICE: 'value_of_service',
	REVENUE: 'revenue',
	COST: 'cost',
	MARKUP: 'markup',
	PROFIT: 'profit',
	MARGIN: 'margin',
	RATE_PER_HOUR: 'rate_per_hour',
	COST_PER_HOUR: 'cost_per_hour',
};

export const EXPORT_COLUMNS = [
	PHASE_COLUMNS.PHASE_NAME,
	PHASE_COLUMNS.DELIVERABLE_NAME,
	PHASE_COLUMNS.ROLE,
	PHASE_COLUMNS.HOURS,
	PHASE_COLUMNS.EXPENSE_NAME,
	PHASE_COLUMNS.EXPENSE_CATEGORY,
];

const TIME_ESTIMATES_COLUMNS = [
	PHASE_COLUMNS.VALUE_OF_SERVICE,
	PHASE_COLUMNS.REVENUE,
	PHASE_COLUMNS.COST,
	PHASE_COLUMNS.PROFIT,
	PHASE_COLUMNS.RATE_PER_HOUR,
	PHASE_COLUMNS.COST_PER_HOUR,
];
const EXPENSE_ESTIMATES_COLUMNS = [
	PHASE_COLUMNS.REVENUE,
	PHASE_COLUMNS.COST,
	PHASE_COLUMNS.MARKUP,
	PHASE_COLUMNS.PROFIT,
	PHASE_COLUMNS.MARGIN,
];

const TIME_ESTIMATES = 'time_estimates';
const EXPENSE_ESTIMATES = 'expense_estimates';

const EYE_OPTION_COLUMNS = [
	{id: TIME_ESTIMATES, nestedOptions: TIME_ESTIMATES_COLUMNS},
	{id: EXPENSE_ESTIMATES, nestedOptions: EXPENSE_ESTIMATES_COLUMNS},
];

export interface OnSaveError {
	type: OnSaveErrorType;
	message: string;
	phaseId: string;
}

export enum OnSaveErrorType {
	MISSING_ROLE = 'MISSING_ROLE',
}

function getGlobalDeliverableId(item: {node?: {deliverableId?: number | null} | null} | null): string {
	return item?.node?.deliverableId ? encodeGlobalId('DeliverableType', Number(item.node.deliverableId)) : '';
}

export const PHASE_EYE_OPTIONS = EYE_OPTION_COLUMNS.map(column => ({
	name: column.id,
	translationId: 'phase_management.column.' + column.id,
	disabled: false,
	checked: true,
	nestedOptions: column.nestedOptions.map(nestedColumn => ({
		name: nestedColumn,
		translationId: 'phase_management.column.' + nestedColumn,
		disabled: false,
		checked: true,
	})),
}));

export const getInitialBaselineItems = (project: Project | null, useDeliverables: boolean): BaselineItem[] => [
	...(project?.phaseBaselineRoles?.edges
		?.filter(role => role?.node?.role && role?.node?.financialNumbers)
		.map(role => {
			const {
				baselineRevenue,
				baselineCost,
				baselineProfit,
				baselineTimeAndExpenses,
				baselineCostPerHour,
				baselineRatePerHour,
			} = role?.node?.financialNumbers || {};
			const baselineMinutes = role?.node?.baselineMinutes || 0;
			const revenueRaw = baselineRevenue || 0;
			const costRaw = baselineCost || 0;
			const hoursRaw = (baselineMinutes || 0) / 60;
			const profitRaw = baselineProfit || 0;
			const valueOfServiceRaw = baselineTimeAndExpenses || 0;

			const [hours, valueOfService, revenue, cost, profit, ratePerHour, costPerHour]: number[] = [
				hoursRaw,
				valueOfServiceRaw,
				revenueRaw,
				costRaw,
				profitRaw,
				baselineRatePerHour,
				baselineCostPerHour,
			].map(Util.roundFloatToTwoDecimals);

			const globalDeliverableId = useDeliverables ? getGlobalDeliverableId(role) : undefined;

			return {
				phaseBaselineRoleId: role?.node?.id || '',
				projectId: project.id || '',
				phaseId: role?.node?.globalPhaseId || '',
				deliverableId: globalDeliverableId,
				roleId: role?.node?.role?.id || '',
				roleName: role?.node?.role?.name || '',
				hours,
				valueOfService,
				revenue,
				cost,
				profit,
				ratePerHour,
				costPerHour,
			};
		}) || []),
	...(project?.phaseBaselineExpenses?.edges
		?.filter(expense => expense?.node)
		.map(expense => {
			const revenueRaw = expense?.node?.expenseRevenue || 0;
			const costRaw = expense?.node?.expenseCost || 0;

			const [roundedRevenue, roundedCost, roundedMarkup, roundedProfit, roundedMargin]: number[] = [
				revenueRaw,
				costRaw,
				expense?.node?.expenseMarkup || 0,
				expense?.node?.expenseProfit || 0,
				revenueRaw ? (revenueRaw - costRaw) / revenueRaw : 0,
			].map(Util.roundFloatToTwoDecimals);

			const name = expense?.node?.expenseName ?? '';
			const categoryName = expense?.node?.expenseCategory?.name ?? '';
			const categoryId = expense?.node?.expenseCategory?.id ?? '';

			const globalDeliverableId = useDeliverables ? getGlobalDeliverableId(expense) : undefined;

			return {
				phaseBaselineExpenseId: expense?.node?.id || '',
				projectId: project.id || '',
				phaseId: expense?.node?.globalPhaseId || '',
				deliverableId: globalDeliverableId,
				categoryName,
				categoryId,
				revenue: roundedRevenue,
				cost: roundedCost,
				markup: roundedMarkup,
				profit: roundedProfit,
				margin: roundedMargin,
				name,
			};
		}) || []),
];

export const getInitialPhaseDeliverables = (project: Project | null): ProjectPhaseDeliverable[] =>
	project?.phases?.edges
		?.map(phaseEdge =>
			phaseEdge?.node?.phaseDeliverables?.edges
				?.filter(phaseDeliverableEdge => phaseDeliverableEdge?.node && phaseDeliverableEdge?.node.deliverable)
				.map(phaseDeliverableEdge => ({
					id: phaseDeliverableEdge?.node?.id,
					phaseId: phaseDeliverableEdge?.node?.phaseId,
					deliverable: phaseDeliverableEdge?.node?.deliverable,
					financialNumbers: phaseDeliverableEdge?.node?.financialNumbers,
				}))
		)
		.flat(2)
		.filter((item): item is ProjectPhaseDeliverable => !!item) || [];

export const createNodeIdLookup = <T extends {id: string}>(nodes: (T | null | undefined)[]): Map<string, T> =>
	new Map<string, T>(nodes.filter(Boolean).map((d: T): [string, T] => [d.id, d]));

export function formatAndExportCSV(
	columnNames: string[],
	filename: string,
	initialBaselineItems: BaselineItem[],
	phaseLookup: Map<string, Phase>,
	deliverableLookup: Map<string, Deliverable>
): void {
	const fullFileName = filename + '.csv';

	function getDeliverableName(item: BaselineItem) {
		return item.deliverableId && deliverableLookup.get(item.deliverableId)?.name;
	}

	const mappedRows = initialBaselineItems.map((item: BaselineItem) => {
		const mappings = {
			[PHASE_COLUMNS.PHASE_NAME]: phaseLookup.get(item.phaseId)?.name,
			[PHASE_COLUMNS.DELIVERABLE_NAME]: getDeliverableName(item),
			[PHASE_COLUMNS.ROLE]: item.roleName,
			[PHASE_COLUMNS.HOURS]: item.hours,
			[PHASE_COLUMNS.VALUE_OF_SERVICE]: item.valueOfService,
			[PHASE_COLUMNS.REVENUE]: item.revenue,
			[PHASE_COLUMNS.COST]: item.cost,
			[PHASE_COLUMNS.PROFIT]: item.profit,
			[PHASE_COLUMNS.RATE_PER_HOUR]: item.ratePerHour,
			[PHASE_COLUMNS.COST_PER_HOUR]: item.costPerHour,
			[PHASE_COLUMNS.EXPENSE_NAME]: item.name,
			[PHASE_COLUMNS.EXPENSE_CATEGORY]: item.categoryName,
			[PHASE_COLUMNS.MARKUP]: item.markup,
			[PHASE_COLUMNS.MARGIN]: item.margin,
		};
		return columnNames.map(header => {
			const value = `${mappings[header] ?? ''}`;
			return value.includes(',') ? `"${value}"` : value;
		});
	});

	const exportData = [columnNames, ...mappedRows].join('\r\n');

	Util.exportToCSV(exportData, fullFileName);
}

export const getTotals = (baselineItems: BaselineItem[]): BaselineTotals => {
	const totals = baselineItems.reduce(
		(result, currentItem) => {
			if ('phaseBaselineRoleId' in currentItem) {
				const item = currentItem as RoleRow;
				result.baselineMinutes += item.hours * 60;
				result.baselineCost += item.cost;
				result.baselineRevenue += item.revenue;
				result.baselineTimeAndExpenses += item.valueOfService;
			} else if ('phaseBaselineExpenseId' in currentItem) {
				const item = currentItem as ExpenseRow;
				result.baselineCost += item.cost;
				result.baselineRevenue += item.revenue;
				result.baselineTimeAndExpenses += Math.max(item.revenue, item.cost);
			}
			return result;
		},
		{
			baselineCost: 0,
			baselineMargin: 0,
			baselineMinutes: 0,
			baselineProfit: 0,
			baselineRevenue: 0,
			baselineTimeAndExpenses: 0,
		}
	);

	totals.baselineProfit = totals.baselineRevenue - totals.baselineCost;
	totals.baselineMargin = totals.baselineRevenue
		? Util.roundFloatToTwoDecimals(totals.baselineProfit / totals.baselineRevenue)
		: 0;
	return totals;
};
